import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, Package } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsBell() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/notifications?limit=10`),
        axios.get(`${API}/notifications/unread-count`),
      ]);
      setItems(Array.isArray(list.data) ? list.data : []);
      setUnread((count.data && count.data.count) || 0);
    } catch (e) { /* not logged in or endpoint missing */ }
  }, [user]);

  useEffect(() => {
    refresh();
    // Poll every 30s while tab is active
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const handleOpen = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && unread > 0) {
      // Optimistically reset unread and mark all as read server-side
      setUnread(0);
      try {
        await axios.put(`${API}/notifications/read-all`, {});
      } catch (e) { /* ignore */ }
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-[#F3F2EE] transition-colors"
          data-testid="notifications-bell"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-[#121212]" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 min-w-[18px] text-center leading-[18px]"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="px-4 py-3 border-b border-[#E5E4E0] flex items-center justify-between">
          <h4 className="font-outfit font-semibold text-[#121212]">
            {language === 'lv' ? 'Paziņojumi' : 'Notifications'}
          </h4>
          {items.length > 0 && (
            <span className="text-xs text-[#5C5C5C]">
              <Check className="w-3 h-3 inline mr-1" />
              {language === 'lv' ? 'Visi izlasīti' : 'All read'}
            </span>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#5C5C5C]">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            {language === 'lv' ? 'Nav paziņojumu' : 'No notifications'}
          </div>
        ) : (
          <div className="divide-y divide-[#F3F2EE]">
            {items.map((n) => {
              const Icon = n.kind === 'order' ? Package : Bell;
              const href = n.link || (n.order_id ? `/orders` : '#');
              return (
                <Link
                  key={n.id}
                  to={href}
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 hover:bg-[#F3F2EE] transition-colors ${n.read ? '' : 'bg-[#FFF8F3]'}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${n.read ? 'bg-[#F3F2EE]' : 'bg-[#D97736]/10'}`}>
                      <Icon className={`w-4 h-4 ${n.read ? 'text-[#5C5C5C]' : 'text-[#D97736]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#121212] line-clamp-1">{n.title}</p>
                      <p className="text-xs text-[#5C5C5C] line-clamp-2 mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-[#5C5C5C] mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString(language === 'lv' ? 'lv-LV' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
